import React, { useRef, useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from "react-redux";
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { FileUpload } from 'primereact/fileupload';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Toast } from 'primereact/toast';
import { useTranslation } from "react-i18next";
import "../../Styles/pages/settings.scss";
import "../../Styles/pages/customers.scss";
import PaySwitchActions from "../../Redux/transition/payswitch/payswitch.reducer";
import junipayImageTip from "../../assets/images/junipay_pp_keys_tip.png";
import junipayKeyTip from "../../assets/images/junipay_keys.png";
import Loading from '../../Components/Loading';
import BusinessActions from "../../Redux/actions/business-actions"
import DocumentActions from '../../Redux/transition/document-upload/document-upload.reducer';
import { useFormik } from "formik";
import { CustomValidationMessage, CustomInfoMessage } from "../../Components/CustomMessage";
import { uploadProviderPrivateKey } from "../../Service/api";
import { isCredentialOverlayLocked } from "../../utils/paymentProviderStatus";

const PROVIDER = "JUNIPAY";

function PaymentKeysSettingsJunipay({ onSave, onCredentialsSaved, credentialsUnlocked = false }) {

    const dispatch = useDispatch();
    const createCredentials = useCallback((businessId, pmtProvider, credentials) => { dispatch(PaySwitchActions.paySwitchCredentialCreateRequest(businessId, pmtProvider, credentials)) }, [dispatch]);
    const getCredentials = useCallback((businessId, provider) => { dispatch(PaySwitchActions.paySwitchCredentialRequest(businessId, provider)) }, [dispatch]);
    const getBusiness = useCallback((businessId) => { dispatch(BusinessActions.businessRequest(businessId)) }, [dispatch])
    const getVerificationRecord = useCallback((businessId) => { dispatch(DocumentActions.businessVerificationStatusRequest(businessId)) }, [dispatch]);

    const defMembership = useSelector((state) => state.defMembership.defMembership);
    const paymentCredentials = useSelector((state) => state.payswitch.credentials);
    const fetchingPaymentCredentials = useSelector((state) => state.payswitch.fetchingCredentials);
    const creating = useSelector((state) => state.payswitch.creating);
    const credentials = useSelector((state) => state.payswitch.createdCredentials);
    const credentialsError = useSelector((state) => state.payswitch.errorCreatingCredentials);
    const business = useSelector((state) => state.businesses.business);
    const hasOwnCredentials = paymentCredentials?.provider === PROVIDER;
    const overlayLocked = isCredentialOverlayLocked(PROVIDER, business, credentialsUnlocked);

    const op = useRef(null);
    const op1 = useRef(null);
    const toast = useRef(null);
    const privateKeyFileUploadRef = useRef(null);
    const onCredentialsSavedRef = useRef(onCredentialsSaved);
    const { t } = useTranslation("common");
    const [create, setCreate] = useState(false)
    const [save, setSave] = useState(false)
    const [clicked, setClicked] = useState(false)
    const [editTokens, setEditTokens] = useState(false)
    const [toastShown, setToastShown] = useState(false)
    const [errorToastShown, setErrorToastShown] = useState(false)
    const [submit, setSubmit] = useState(0);
    const [selectedKeyFile, setSelectedKeyFile] = useState(null)
    const [uploadingPrivateKey, setUploadingPrivateKey] = useState(false)
    const [privateKeyFileError, setPrivateKeyFileError] = useState(false)
    const [privateKeySaved, setPrivateKeySaved] = useState(false)

    const hasPrivateKeyOnRecord = Boolean(
        paymentCredentials?.hasPrivateKey
        || paymentCredentials?.privateKeyUploaded
        || paymentCredentials?.privateKeyPresent
    );
    const showPrivateKeySavedAlert = privateKeySaved || hasPrivateKeyOnRecord;

    const canShowPrivateKeySection = !editTokens && !creating
        && (hasOwnCredentials || toastShown);
    const fieldsDisabled = ((business?.pmtProvider === PROVIDER && hasOwnCredentials) && !editTokens) || toastShown;

    const handleClearPrivateKeyFile = () => {
        setSelectedKeyFile(null);
        setPrivateKeyFileError(false);
        privateKeyFileUploadRef.current?.clear();
    };

    const handlePrivateKeyFileSelect = (event) => {
        const file = event.files?.[0];
        if (!file) {
            setSelectedKeyFile(null);
            setPrivateKeyFileError(false);
            return;
        }

        if (!file.name.toLowerCase().endsWith(".key")) {
            setSelectedKeyFile(null);
            setPrivateKeyFileError(true);
            privateKeyFileUploadRef.current?.clear();
            return;
        }

        setSelectedKeyFile(file);
        setPrivateKeyFileError(false);
    };

    const handlePrivateKeyUpload = async () => {
        if (!selectedKeyFile) {
            setPrivateKeyFileError(true);
            return;
        }

        const formData = new FormData();
        formData.append("file", selectedKeyFile, selectedKeyFile.name);
        setUploadingPrivateKey(true);

        try {
            const response = await uploadProviderPrivateKey(defMembership.business.id, "JUNIPAY", formData);
            if (response.ok) {
                handleClearPrivateKeyFile();
                setPrivateKeySaved(true);
                getCredentials(defMembership.business.id, "JUNIPAY");
                toast.current?.show({
                    severity: "success",
                    summary: t("business.payment_success"),
                    detail: t("business.upload_private_key_success"),
                    life: 3000,
                });
            } else {
                toast.current?.show({
                    severity: "error",
                    summary: t("business.payment_error"),
                    detail: t("business.upload_private_key_error"),
                    life: 3000,
                });
            }
        } catch {
            toast.current?.show({
                severity: "error",
                summary: t("business.payment_error"),
                detail: t("business.upload_private_key_error"),
                life: 3000,
            });
        } finally {
            setUploadingPrivateKey(false);
        }
    };

    const handleChange = (newValue) => {
        setSave(newValue);
        onSave?.(newValue);
    };

    useEffect(() => {
        onCredentialsSavedRef.current = onCredentialsSaved;
    }, [onCredentialsSaved]);

    useEffect(() => {
        if (hasPrivateKeyOnRecord) {
            setPrivateKeySaved(true);
        }
    }, [hasPrivateKeyOnRecord]);

    useEffect(() => {
        if (create && !creating && credentials) {
            setClicked(false)
            setToastShown(true)
            setCreate(false)
            setEditTokens(false)
            setSubmit(0)
            onCredentialsSavedRef.current?.()
            getCredentials(defMembership.business.id, "JUNIPAY")
            getVerificationRecord(defMembership.business.id)
            getBusiness(defMembership.business.id)
            formik.setFieldValue("apiUser", "********")
            formik.setFieldValue("apiKey", "*********")
            formik.setFieldValue("merchantId", credentials?.merchantId ?? formik.values.merchantId)
        }
        if (create && !creating && credentialsError) {
            setErrorToastShown(true)
            setCreate(false)
            formik.setFieldValue("apiUser", "")
            formik.setFieldValue("apiKey", "")
            formik.setFieldValue("merchantId", "")
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [create, creating, credentials, credentialsError])


    useEffect(() => {
        if (submit > 0) {
            formik.handleSubmit()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submit])

    const formik = useFormik({
        initialValues: {
            apiUser: (paymentCredentials && paymentCredentials?.provider === "JUNIPAY") || (editTokens) ? "********" : "",
            apiKey: (paymentCredentials && paymentCredentials?.provider === "JUNIPAY") || (editTokens) ? "*********" : "",
            merchantId: (paymentCredentials && paymentCredentials?.provider === "JUNIPAY") || (editTokens) ? paymentCredentials?.merchantId : "",
        },

        validate: (data) => {
            let errors = {};
            if (!data.merchantId && submit > 0) {
                errors.merchantId = t("business.payment_serviceId_required");
            }
            if (!data.apiUser && submit > 0) {
                errors.apiUser = t("business.appsNmobile_username_required")
            }
            if (!data.apiKey && submit > 0) {
                errors.apiKey = t("business.payment_secret_token_required")
            }
            return errors;
        },

        onSubmit: data => {
            const credentialsPayload = {
                ...data,
                provider: "JUNIPAY"
            }
            createCredentials(defMembership.business.id, credentialsPayload)
            setCreate(true)
        }
    })

    return (

        <>{fetchingPaymentCredentials && !credentialsUnlocked ? <Loading /> :
            <div>
                <OverlayPanel ref={op} className="payment-keys-overlay">
                    <div className='flex flex-column'>
                        <img src={junipayImageTip} alt={t("business.payment_op1")} style={{ width: "100%", maxWidth: "700px" }} />
                        <img src={junipayKeyTip} alt={t("business.payment_op1")} style={{ width: "100%", maxWidth: "700px" }} />
                    </div>
                </OverlayPanel>

                <OverlayPanel ref={op1} className="payment-keys-overlay">
                    <div>
                        <img src={junipayKeyTip} alt={t("business.payment_op1")} style={{ width: "100%", maxWidth: "850px" }} />
                    </div>
                </OverlayPanel>

                <Toast ref={toast} onRemove={(message) => {
                    if (message.severity === 'success' && message.detail !== t("business.upload_private_key_success")) {
                        getCredentials(defMembership.business.id, "JUNIPAY")
                        getVerificationRecord(defMembership.business.id)
                        getBusiness(defMembership.business.id)
                        formik.resetForm()
                        handleChange(!save)
                        setClicked(false)
                        setSubmit(0)
                    }
                }} />

                <div className="form-container">
                    <form onSubmit={formik.handleSubmit} className="wizard-forms-wrapper p-grid p-nogutter settings-edit-form">
                        <div className="card payment_card">
                            <div className="grid">
                                <div className='hidden md:block col-2'>
                                </div>

                                <div className='col-12 md:col-8'>
                                    <div className="info-icon-div">
                                        <i className="pi pi-info-circle info-icon info-icon-i"
                                            onClick={(e) => { op.current.toggle(e) }}></i>
                                        <span className='payment_token'>{t("business.payment_merchant_info")}</span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid inputs-mb">
                                <div className='col-12 md:col-2'>
                                    <label
                                        id="merchantId-label"
                                        htmlFor="merchantId"
                                        className={"label"}>
                                        {t("business.payment_token_link")}
                                        <span className="isterik">*</span>
                                    </label>
                                </div>

                                <div className='col-12 md:col-8 apiUser-div-width'>
                                    <InputText
                                        id="merchantId"
                                        name="merchantId"
                                        type='text'
                                        value={formik?.values.merchantId}
                                        onChange={formik?.handleChange}
                                        placeholder={t("business.payment_copy_paste_txt")}
                                        disabled={fieldsDisabled}
                                    />
                                </div>
                                {formik.errors.merchantId && formik.errors.merchantId &&
                                    <CustomValidationMessage
                                        className='field_height'
                                        severity="error"
                                        text={t("business.payment_serviceId_required")} />
                                }
                            </div>

                            <hr className="hr_mr" />

                            <div className='inner-payment-card'>
                                <div className='payment-credential'>{t("business.payment_credential")}</div>

                                <div className="grid api-div">
                                    <div className='hidden md:block col-2'>
                                    </div>
                                    <div className='col-12 md:col-8'>
                                        <div className='icon-main-div'><i className="pi pi-info-circle info-icon info-icon-i1"
                                            onClick={(e) => { op1.current.toggle(e) }}></i> <span className='payment_token'>{t("business.payment_token_info")}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="grid inputs-mb api-div">
                                    <div className='col-12 md:col-2'>
                                        <label
                                            id="apiUser-label"
                                            htmlFor="apiUser"
                                            className={"label"}>
                                            {t("business.payment_clientId")}
                                            <span className="isterik">*</span>
                                        </label>
                                    </div>

                                    <div className='col-12 md:col-8 apiUser-div-width'>
                                        <InputText
                                            id="apiUser"
                                            name="apiUser"
                                            type='text'
                                            autoComplete="off"
                                            value={formik?.values.apiUser.replace(/./g, '•')}
                                            onChange={formik?.handleChange}
                                            placeholder={t("business.payment_copy_paste_txt")}
                                            disabled={fieldsDisabled}
                                        />
                                    </div>
                                    {formik.errors.apiUser && formik.errors.apiUser &&
                                        <CustomValidationMessage
                                            className='field_height'
                                            severity="error"
                                            text={t("business.appsNmobile_username_required")} />
                                    }
                                </div>

                                <div className="grid inputs-mb">
                                    <div className='col-12 md:col-2'>
                                        <label
                                            id="apiKey-label"
                                            htmlFor="apiKey"
                                            className={"label"}>
                                            {t("business.payment_secret")}
                                            <span className="isterik">*</span>
                                        </label>
                                    </div>

                                    <div className='col-12 md:col-8 apiUser-div-width'>
                                        <InputText
                                            id="apiKey"
                                            name="apiKey"
                                            type='text'
                                            autoComplete="off"
                                            value={formik?.values.apiKey.replace(/./g, '•')}
                                            onChange={formik?.handleChange}
                                            placeholder={t("business.payment_copy_paste_txt")}
                                            disabled={fieldsDisabled}
                                        />
                                    </div>

                                    {formik.errors.apiKey && formik.touched.apiKey &&
                                        <CustomValidationMessage
                                            className='field_height'
                                            severity="error"
                                            text={t("business.payment_secret_token_required")} />
                                    }

                                    {(((paymentCredentials && paymentCredentials?.provider === "JUNIPAY") && !editTokens) ||
                                        ((paymentCredentials && paymentCredentials?.provider === "JUNIPAY") && editTokens && toastShown))
                                        && <div className='col-12 md:col-1 inner-edit'>
                                            <Button
                                                id='innerEdit-btn'
                                                label="Edit"
                                                type='button'
                                                text
                                                severity='info'
                                                onClick={() => {
                                                    setToastShown(false)
                                                    setErrorToastShown(false)
                                                    setEditTokens(true)
                                                    formik.setFieldValue("apiUser", "")
                                                    formik.setFieldValue("apiKey", "")
                                                    formik.setFieldValue("merchantId", "")
                                                    formik.handleChange(false)
                                                }}
                                                loading={creating}
                                            />
                                        </div>}
                                </div>
                            </div>

                            {canShowPrivateKeySection && (
                                <>
                                    <hr className="hr_mr" />
                                    <div className="grid inputs-mb junipay-private-key-section">
                                        <div className="col-12 md:col-2">
                                            <label
                                                id="privateKey-label"
                                                className="label"
                                            >
                                                {t("business.upload_private_key_optional_heading")}
                                            </label>
                                        </div>
                                        <div className="col-12 md:col-8 apiUser-div-width">
                                            {showPrivateKeySavedAlert && (
                                                <div className="payment-added-alert col-12 mb-3">
                                                    <span className="payment-added-alert1">
                                                        {t("business.existing_payment_junipay")}
                                                    </span>
                                                    <br />
                                                    <span className="payment-added-alert2">
                                                        {t("business.existing_payment_key_hint")}
                                                    </span>
                                                </div>
                                            )}
                                            <p className="junipay-private-key-help mb-3">{t("business.upload_private_key_inline_help")}</p>
                                            <div className="junipay-private-key-actions flex align-items-center flex-wrap gap-2">
                                                <FileUpload
                                                    ref={privateKeyFileUploadRef}
                                                    mode="basic"
                                                    name="file"
                                                    className="import-csv-upload align-content-end"
                                                    accept=".key"
                                                    customUpload
                                                    chooseLabel={t("business.upload_private_key_choose")}
                                                    disabled={uploadingPrivateKey}
                                                    onSelect={handlePrivateKeyFileSelect}
                                                    emptyTemplate={<p className="m-0"></p>}
                                                />
                                                <Button
                                                    label={t("business.upload_private_key")}
                                                    type="button"
                                                    className="save-button p-button-info"
                                                    onClick={handlePrivateKeyUpload}
                                                    loading={uploadingPrivateKey}
                                                    disabled={!selectedKeyFile || uploadingPrivateKey}
                                                />
                                                <Button
                                                    label={t("cancel")}
                                                    type="button"
                                                    className="cancel-button p-button"
                                                    onClick={handleClearPrivateKeyFile}
                                                    disabled={uploadingPrivateKey}
                                                />
                                            </div>
                                            {privateKeyFileError && (
                                                <CustomValidationMessage
                                                    className="field_height mt-2"
                                                    severity="error"
                                                    text={t("business.upload_private_key_invalid_file")}
                                                />
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div >

                        <div id="bottom_buttons_div">
                            <div className='cancel'>
                                {(((paymentCredentials && paymentCredentials?.provider === "JUNIPAY") && clicked && !editTokens) ||
                                    ((paymentCredentials && paymentCredentials?.provider === "JUNIPAY") &&
                                        editTokens && !toastShown && !errorToastShown)) && <Button
                                        className="cancel-button p-button"
                                        id='edit_butto'
                                        label="Cancel"
                                        onClick={() => {
                                            setEditTokens(false)
                                            setToastShown(false)
                                            formik.resetForm()
                                            setErrorToastShown(false)
                                            setClicked(false)
                                            formik.setFieldValue("apiUser", "**********")
                                            formik.setFieldValue("apiKey", "**********")
                                            formik.setFieldValue("merchantId", paymentCredentials?.merchantId)
                                        }}
                                        type='button'
                                        loading={creating}
                                    />}
                            </div>

                            {creating &&
                                <CustomInfoMessage
                                    severity="warn"
                                    summary=""
                                    detail={t("business.payment_progress_info")}
                                />
                            }

                            {(errorToastShown) &&
                                <CustomInfoMessage
                                    severity="error"
                                    summary=""
                                    detail={t("business.payment_error_info")}
                                />
                            }

                            <div>
                                {(((paymentCredentials && paymentCredentials?.provider === "JUNIPAY") && clicked && !editTokens) ||
                                    ((paymentCredentials && paymentCredentials?.provider === "JUNIPAY") && editTokens && !toastShown && !errorToastShown)
                                    || !paymentCredentials || (paymentCredentials && paymentCredentials?.provider !== "JUNIPAY")) &&
                                    <Button
                                        onClick={() => setSubmit(1)}
                                        className="save-button p-button-info"
                                        id='edit_button'
                                        label="Save"
                                        type='submit'
                                        loading={creating}
                                    />}
                            </div>
                        </div>
                    </form >
                    <div id={overlayLocked ? "overlay" : undefined}>
                    </div>
                </div>
            </div >
        }
        </>
    )
}

export default PaymentKeysSettingsJunipay;
